/**
 * Regalia: the ornaments that make a school's uniform its own — a taiji on a Taoist's back, a
 * monk's prayer beads, the guard's gilt waist token, a jade pendant on a silk cord, a cloud collar
 * over a lady's shoulders, sash tails streaming behind, an embroidered flying fish, gold armlets.
 * Each hangs on the skeleton like any garment, so it follows every pose and camera angle, and the
 * loose ones (tassels, sash tails) sway with the figure's movement.
 */
import { flutter, type Layer, lerp, type Piece, px, torsoWidths, zOf } from './figure';
import { printDir } from './leftArt';
import type { Side, Skeleton } from './skeleton';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

export type RegaliaId =
  'taiji' | 'mala' | 'jade' | 'token' | 'cloudCollar' | 'sashTails' | 'flyingFish' | 'armlets' | 'tassels';

/** One ornament: what it is and its colours (main, shade, and a second colour such as a tassel's). */
export interface Regalia {
  id: RegaliaId;
  c: string;
  s: string;
  a: string;
}

const INK = '#16121c';

/** Where the middle of the chest faces on screen this frame (a little toward the facing side). */
const front = (sk: Skeleton): number => {
  const j = sk.joints;
  return Math.round(j.chest.x + (sk.square ? 0 : sk.view === 'side' ? 2 : 1));
};

/** The point on the waist where things hang from the sash, on the near side (A) or the far side. */
function hangPoint(sk: Skeleton, side: Side): Pt {
  const j = sk.joints;
  const [, whw] = torsoWidths(sk);
  if (sk.square)
    return { x: Math.round(j.waist.x + (side === 'A' ? -1 : 1) * (whw - 1)), y: Math.round(j.waist.y) };
  const dir = side === 'A' ? 1 : -1;
  return { x: Math.round(j.waist.x + dir * (sk.view === 'side' ? 1 : whw - 2)), y: Math.round(j.waist.y) };
}

/** A tassel: a knot, then threads hanging `len` rows from `top`, swaying as the figure moves. */
function tassel(g: G, sk: Skeleton, top: Pt, len: number, c: string, s: string, knot: string): void {
  px(g, top.x - 1, top.y, 3, 1, knot);
  for (let i = 1; i <= len; i++) {
    const { dx, dy } = flutter(sk, i / len, i, 0.8);
    const w = i > len - 3 ? 3 : 1;
    px(g, top.x - (w > 1 ? 1 : 0) + dx, top.y + i + dy, w, 1, i === len ? s : c);
  }
}

/**
 * A yin-yang disc of radius `r` centred at (cx, cy); `dir` -1 draws it mirrored, for a left-facing view, so the flip
 * shows it the right way round (rig/leftArt.ts `printDir`).
 */
function taijiDisc(g: G, cx: number, cy: number, r: number, light: string, dark: string, dir = 1): void {
  const small = r / 2;
  for (let y = -Math.ceil(r); y <= Math.ceil(r); y++)
    for (let x = -Math.ceil(r); x <= Math.ceil(r); x++) {
      const px0 = (x + 0.5 - (r % 1 ? 0.5 : 0)) * dir;
      const py0 = y + 0.5 - (r % 1 ? 0.5 : 0);
      const d = Math.hypot(px0, py0);
      if (d > r + 0.2) continue;
      let col: string;
      if (d > r - 0.8) col = INK;
      else {
        const up = Math.hypot(px0, py0 + small);
        const down = Math.hypot(px0, py0 - small);
        // Light on the left, dark on the right, each swelling into the other's half.
        col = up < small ? light : down < small ? dark : px0 < 0 ? light : dark;
        if (up < small * 0.35) col = dark;
        if (down < small * 0.35) col = light;
      }
      px(g, cx + x, cy + y, 1, 1, col);
    }
}

function taiji(sk: Skeleton, o: Regalia): Piece[] {
  const j = sk.joints;
  // Large between the shoulders on the back; a small one on the breast in front.
  return [
    {
      z: zOf(sk, 'chest', 0.09),
      draw: (g) => {
        const midY = Math.round(j.chest.y + (j.waist.y - j.chest.y) * 0.45);
        if (sk.back) taijiDisc(g, Math.round(j.chest.x), midY, 4.5, o.c, o.s, printDir(sk));
        else if (sk.view !== 'side') {
          const a = Math.sign(j.shoulderB.x - j.chest.x) || -1;
          taijiDisc(g, front(sk) + a * 3, Math.round(j.chest.y) + 3, 2.5, o.c, o.s, printDir(sk));
        }
      },
    },
  ];
}

/** Prayer beads: big wooden beads round the neck and down the chest, a red bead and a tassel at the bottom. */
function mala(sk: Skeleton, o: Regalia): Piece[] {
  const j = sk.joints;
  const [shw] = torsoWidths(sk);
  return [
    {
      z: zOf(sk, 'chest', 0.09),
      draw: (g) => {
        const top = Math.round(j.chest.y) - 1;
        if (sk.back) {
          for (let x = -3; x <= 3; x += 2) px(g, Math.round(j.chest.x) + x, top, 2, 1, x % 4 ? o.c : o.s);
          return;
        }
        const cx = front(sk);
        const hw = sk.view === 'side' ? 2 : Math.max(3, shw - 1);
        const drop = 8;
        const bottom = { x: cx, y: top + drop };
        let i = 0;
        for (let t = -1; t <= 1.001; t += 0.12, i++) {
          const x = cx + t * hw - (sk.view === 'side' ? (1 - Math.abs(t)) * -1 : 0);
          const y = top + (1 - t * t) * drop;
          px(g, x, y, 2, 2, i % 2 ? o.c : o.s);
          px(g, x, y, 1, 1, i % 2 ? '#f0d8a8' : o.c);
        }
        // The guru bead and its tassel.
        px(g, bottom.x - 1, bottom.y + 1, 3, 3, o.a);
        px(g, bottom.x - 1, bottom.y + 1, 1, 1, '#ffb0a0');
        tassel(g, sk, { x: bottom.x, y: bottom.y + 4 }, 4, o.a, o.s, '#f0c040');
      },
    },
  ];
}

/** A jade disc (bi) on a cord from the sash, a tassel below it. */
function jade(sk: Skeleton, o: Regalia): Piece[] {
  return [
    {
      z: sk.back ? zOf(sk, 'chest', -0.2) : zOf(sk, 'chest', 0.09),
      draw: (g) => {
        const h = hangPoint(sk, 'A');
        px(g, h.x, h.y, 1, 3, o.a);
        const d = { x: h.x, y: h.y + 4 };
        px(g, d.x - 1, d.y - 1, 3, 3, o.c);
        px(g, d.x - 1, d.y + 1, 3, 1, o.s);
        px(g, d.x, d.y, 1, 1, INK);
        px(g, d.x - 1, d.y - 1, 1, 1, '#f0fff4');
        tassel(g, sk, { x: d.x, y: d.y + 2 }, 6, o.a, o.s, o.c);
      },
    },
  ];
}

/** The imperial guard's gilt waist token (腰牌) hanging from the belt on a red tassel. */
function token(sk: Skeleton, o: Regalia): Piece[] {
  return [
    {
      z: sk.back ? zOf(sk, 'chest', -0.2) : zOf(sk, 'chest', 0.09),
      draw: (g) => {
        const h = hangPoint(sk, 'B');
        px(g, h.x, h.y + 1, 1, 2, o.a);
        const t = { x: h.x - 1, y: h.y + 3 };
        px(g, t.x, t.y, 4, 5, o.c);
        px(g, t.x, t.y + 4, 4, 1, o.s);
        px(g, t.x + 3, t.y, 1, 5, o.s);
        px(g, t.x + 1, t.y + 1, 1, 3, INK);
        px(g, t.x, t.y, 1, 1, '#fff0b0');
        tassel(g, sk, { x: t.x + 1, y: t.y + 5 }, 5, o.a, o.s, o.c);
      },
    },
  ];
}

/**
 * A cloud collar (云肩): a capelet over the shoulders, its edge cut into cloud lobes and trimmed,
 * a bead at the point of each lobe.
 */
function cloudCollar(sk: Skeleton, o: Regalia): Piece[] {
  const j = sk.joints;
  const [shw] = torsoWidths(sk);
  const z = Math.max(j.chest.z, j.shoulderA.z, j.shoulderB.z) + 0.09;
  return [
    {
      z,
      draw: (g) => {
        const top = Math.round(j.chest.y) - 2;
        const cx = Math.round(j.chest.x);
        const hw = Math.round((sk.view === 'side' ? shw + 1 : shw + 3) as number);
        const depth = sk.view === 'side' ? 4 : 5;
        for (let x = -hw; x <= hw; x++) {
          const k = (x + hw) / 2.5;
          // Lobes: deeper at the middle of each scallop.
          const lobe = Math.round(Math.cos(k * Math.PI) * 1.2);
          const edgeAt = depth + lobe - (Math.abs(x) > hw - 2 ? 1 : 0);
          for (let y = 0; y <= edgeAt; y++) {
            const col = y === edgeAt ? o.a : y === edgeAt - 1 ? o.s : o.c;
            px(g, cx + x, top + y, 1, 1, col);
          }
          if (lobe === 1 && Math.abs(x) % 2 === 0) px(g, cx + x, top + edgeAt + 1, 1, 1, o.a);
        }
        // The fastening at the throat in front.
        if (!sk.back) px(g, front(sk) - 1, top, 3, 2, o.a);
        px(g, cx - hw, top, hw * 2 + 1, 1, o.s);
      },
    },
  ];
}

/** Two long sash ends tied at the back of the waist, streaming out behind, frayed at the tips. */
function sashTails(sk: Skeleton, o: Regalia): Piece[] {
  const j = sk.joints;
  const [, whw] = torsoWidths(sk);
  const draw = (g: G) => {
    const start = {
      x: Math.round(j.waist.x + (sk.square ? 0 : sk.view === 'side' ? -whw : -2)),
      y: Math.round(j.waist.y) - 1,
    };
    for (const [n, len, off] of [
      [0, 20, -1],
      [1, 15, 2],
    ] as const) {
      for (let i = 0; i <= len; i++) {
        const k = i / len;
        const { dx, dy } = flutter(sk, k, i, 1.25);
        const x = start.x + (sk.square ? off + (n ? 1 : -1) * k * 2 : -i * 0.3 + off * 0.5) + dx;
        const y = start.y + i + dy;
        // Frayed ends: every other thread stops short.
        if (i > len - 2 && i % 2) continue;
        px(g, x, y, 2, 1, i === len ? o.a : i % 5 === 4 ? o.s : o.c);
      }
    }
    // The knot.
    px(g, start.x - 1, start.y - 1, 3, 3, o.s);
    px(g, start.x, start.y, 1, 1, o.c);
  };
  return [{ z: sk.back ? zOf(sk, 'chest', 0.09) : -0.96, draw }];
}

/**
 * The flying fish (飛魚) embroidered in gold thread: a coiling dragon-fish on the breast, its head
 * up by the shoulder, its scaled body curling down to a forked tail; a larger one on the back.
 */
function flyingFish(sk: Skeleton, o: Regalia): Piece[] {
  const j = sk.joints;
  const [shw] = torsoWidths(sk);
  return [
    {
      z: zOf(sk, 'chest', 0.09),
      draw: (g) => {
        if (sk.view === 'side') return;
        const cx = sk.back ? Math.round(j.chest.x) : front(sk);
        // Embroidery: on a left-facing view the mirror image's, so the flip shows it as sewn (rig/leftArt.ts).
        const d = printDir(sk);
        const top = Math.round(j.chest.y) + 1;
        const h = Math.round(j.waist.y) - top - 1;
        const w = Math.max(2, shw - 2) * (sk.back ? 1 : 0.8);
        const pts: Pt[] = [];
        for (let i = 0; i <= 18; i++) {
          const t = i / 18;
          pts.push({ x: cx + d * Math.sin(t * Math.PI * 1.6 + 0.4) * w, y: top + t * h });
        }
        /** A run `w` wide starting `dx` from `x`, mirrored about `x` when `d` is -1. */
        const run = (x: number, dx: number, y: number, w: number, c: string) =>
          px(g, d > 0 ? x + dx : x - dx - w + 1, y, w, 1, c);
        pts.forEach((p, i) => {
          run(p.x, 0, p.y, 2, i % 3 === 0 ? o.s : o.c);
          if (i % 4 === 2) run(p.x, 2, p.y, 1, o.a);
        });
        // The head with its eye and whiskers, and the forked tail.
        const head = pts[0]!;
        run(head.x, -1, head.y - 1, 3, o.c);
        run(head.x, -1, head.y, 3, o.c);
        run(head.x, 0, head.y - 1, 1, INK);
        run(head.x, 2, head.y - 2, 2, o.c);
        const tail = pts[pts.length - 1]!;
        const end = lerp(pts[pts.length - 3]!, tail, 1.6);
        px(g, end.x - 2, end.y, 1, 1, o.c);
        px(g, end.x + 2, end.y, 1, 1, o.c);
        // Cloud wisps round it.
        run(cx, -w - 1, top + h * 0.6, 2, o.a);
        run(cx, w, top + h * 0.3, 2, o.a);
      },
    },
  ];
}

/** Gold armlets round both upper arms, over the sleeve or the bare arm. */
function armlets(sk: Skeleton, o: Regalia): Piece[] {
  return (['A', 'B'] as Side[]).map((side) => {
    const j = sk.joints;
    const sh = j[`shoulder${side}`];
    const el = j[`elbow${side}`];
    return {
      z: zOf(sk, `shoulder${side}`, 0.06),
      draw: (g) => {
        const at = lerp(sh, el, 0.55);
        const w = 6 + sk.shape.limb;
        ring(g, sh, at, w, o.c);
        ring(g, sh, { x: at.x, y: at.y + 1 }, w, o.s);
        px(g, at.x, at.y, 1, 1, o.a);
      },
    };
  });
}

/** A band across a limb at `at`, square to the bone from `from`. */
function ring(g: G, from: Pt, at: Pt, w: number, c: string): void {
  const dx = at.x - from.x;
  const dy = at.y - from.y;
  const l = Math.max(0.001, Math.hypot(dx, dy));
  const nx = -dy / l;
  const ny = dx / l;
  const n = Math.ceil(w);
  for (let i = 0; i <= n; i++) {
    const k = i / n - 0.5;
    px(g, at.x + nx * k * w - 0.5, at.y + ny * k * w - 0.5, 1, 1, c);
  }
}

/** A pair of silk tassels hanging from the sash on either side of the knot. */
function tassels(sk: Skeleton, o: Regalia): Piece[] {
  return [
    {
      z: sk.back ? zOf(sk, 'chest', -0.2) : zOf(sk, 'chest', 0.09),
      draw: (g) => {
        const a = hangPoint(sk, 'A');
        tassel(g, sk, { x: a.x, y: a.y + 1 }, 9, o.c, o.s, o.a);
        if (sk.square) {
          const b = hangPoint(sk, 'B');
          tassel(g, sk, { x: b.x, y: b.y + 1 }, 7, o.c, o.s, o.a);
        }
      },
    },
  ];
}

const DRAW: Record<RegaliaId, (sk: Skeleton, o: Regalia) => Piece[]> = {
  taiji,
  mala,
  jade,
  token,
  cloudCollar,
  sashTails,
  flyingFish,
  armlets,
  tassels,
};

/** A set of ornaments as one layer. */
export function regaliaLayer(list: readonly Regalia[]): Layer {
  return {
    id: 'regalia',
    label: 'เครื่องประดับสำนัก',
    pieces: (sk) => list.flatMap((o) => DRAW[o.id](sk, o)),
  };
}
